/* eslint-disable react/display-name */
import React from "react";
import styled from "styled-components";
import { SnakeToCamelCaseNested, BookDetailSchema } from "@literati/kids-api";
import {
  Image,
  CheckboxFilled,
  prettyPrintCurrencyFromValue,
  checkboxStyles,
  CheckboxFilledProps,
} from "@literati/public-ui-shared-react";

type BookType = SnakeToCamelCaseNested<BookDetailSchema>;
interface BookCheckoutLineItemProps
  extends Omit<CheckboxFilledProps, "label" | "name"> {
  book: BookType;
  isDisabled?: boolean;
  checked?: boolean;
}

const bookCoverFallBackSrc =
  "https://ctfassets.literati.com/vqtz4cq2w5ts/28qbG2kRvefzvWF5y5rUCN/fead1dfa0c8f2a0d4c2699bbe8a6cc23/Universal.png";

const StyledCheckboxFilled = styled(CheckboxFilled)`
  border-radius: 8px;
  color: black;
  min-height: 72px;
  padding: 1rem;

  .${checkboxStyles?.CheckboxClassName?.LABEL} {
    flex: 2;
    display: flex;
    align-items: center;
    min-height: 72px;
  }
`;

const Cite = styled.cite`
  font-style: normal;
  font-weight: bold;
`;

const Author = styled.span`
  color: ${({ theme }) => theme.colors.warmGray3};
  display: block;
`;

const BookPriceDescription = styled.div`
  margin: 0 0 0 1rem;
  text-overflow: ellipsis;
`;

const Price = styled.span<{
  $showPriceComparison?: boolean;
}>`
  display: block;
  margin-left: auto;
  margin-top: 6px;

  strong {
    margin-left: ${({ $showPriceComparison }) =>
      $showPriceComparison ? "10px" : "0"};
  }

  ${({ theme }) => theme.media.lg} {
    margin-top: 0;
  }
`;

const PriceMobile = styled(Price)`
  ${({ theme }) => theme.media.lg} {
    display: none;
  }
`;
const PriceDesktop = styled(Price)`
  display: none;

  ${({ theme }) => theme.media.lg} {
    display: block;
  }
`;

const Strike = styled.s`
  color: ${({ theme }) => theme.colors.warmGray3};
`;

const getPrice = (bookPrice: BookType["bookPrice"]) => {
  if (!bookPrice) return null;
  const { showPriceComparison, listPrice = "", clubPrice = "" } = bookPrice;
  return showPriceComparison ? (
    <>
      <Strike>{prettyPrintCurrencyFromValue(listPrice)}</Strike>
      <strong>{prettyPrintCurrencyFromValue(clubPrice)}</strong>
    </>
  ) : (
    <strong>{prettyPrintCurrencyFromValue(clubPrice)}</strong>
  );
};

const StyledImage = styled(Image)`
  position: relative;
  flex-shrink: 0;
  height: 64px;
  width: 48px;
`;

const BookCheckoutLineItem = ({
  book: { id, title, author, cover, bookPrice },
  isDisabled = false,
  ...props
}: BookCheckoutLineItemProps) => (
  <StyledCheckboxFilled
    {...props}
    name={`book-${id}`}
    isDisabled={isDisabled}
    label={
      <>
        <StyledImage
          alt={`cover of ${title}`}
          contain
          layout="fill"
          objectFit="contain"
          objectPosition="right"
          src={cover || bookCoverFallBackSrc}
          fallbackSrc={bookCoverFallBackSrc}
        />
        <BookPriceDescription>
          <p>
            <Cite>{title}</Cite>
            <Author>by {author.name}</Author>
            <PriceMobile $showPriceComparison={bookPrice?.showPriceComparison}>
              {bookPrice && getPrice(bookPrice)}
            </PriceMobile>
          </p>
        </BookPriceDescription>
        <PriceDesktop $showPriceComparison={bookPrice?.showPriceComparison}>
          {bookPrice && getPrice(bookPrice)}
        </PriceDesktop>
      </>
    }
  />
);

export default BookCheckoutLineItem;
export type { BookCheckoutLineItemProps, BookType };
